/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Dashboard Container.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Layout, Spin } from 'antd';
import { useLocation } from 'react-router-dom';

// Helpers
import { sortNumber } from '@nephro/commons/helpers/utils';
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';

// Components
import AppRoutes from '@nephro/components/AppRoutes';
import AppHeader from '@nephro/components/AppHeader';
import AppBreadCmb from '@nephro/components/AppBreadCrumb';
import PageHeader from '@nephro/components/PageHeader';
import SiderComponent from './components/SiderComponent';
import DrawerComponent from './components/DrawerComponent';
// Routes
import routes from './routes';

// Dependencies Props
const { Content } = Layout;

function DashboardView({
  actions,
  authActions,
  menuActions,
  user,
  loading,
  patientActions
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const [broken, setBroken] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [routesResult, setRoutesResult] = React.useState([]);
  const [dinamicRoutes, setDinamicRoutes] = React.useState(null);
  const [isStaff, setIsStaff] = React.useState(false);
  const location = useLocation();
  const { signOut } = authActions;
  const { getMainMenuByRole } = menuActions;

  const getMenuItems = (type, response) => {
    if (response.data) {
      setDinamicRoutes(response.data.sort(sortNumber('order')));
    }
  };

  React.useEffect(() => {
    const getUserMenu = async () => {
      const userInfo = await getStorageItem('user');
      const oktaEmailAuthenticated = await getStorageItem(
        'oktaEmailAuthenticated'
      );
      const isPatient = await getStorageItem('isPatient');
      setIsStaff(!isPatient);
      setRoutesResult(routes(isPatient));
      if (oktaEmailAuthenticated !== null) {
        if (userInfo?.role?.role_id !== undefined) {
          getMainMenuByRole({ id: userInfo?.role?.role_id }, getMenuItems);
        }
      } else {
        getMainMenuByRole({ id: userInfo?.id_role }, getMenuItems);
      }

      if (isPatient) {
        const { getPatientIndicators } = patientActions;
        getPatientIndicators({ patientId: userInfo.id }, (_, response) => {
          const { setPatientIndicators } = actions;
          setPatientIndicators(response.data);
        });
      }
    };
    getUserMenu();
  }, []);

  const staffDashboardCondition =
    location.pathname === '/main/dashboard' && isStaff;

  // eslint-disable-next-line prettier/prettier
  const hideDashboardContentView =
    location.pathname.includes('main/videocall/');

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {hideDashboardContentView ? (
        <AppRoutes routes={routesResult} />
      ) : (
        <>
          <DrawerComponent
            setVisible={setVisible}
            onBreak={setBroken}
            onCollapse={setCollapsed}
            visible={visible}
            signOut={signOut}
            routes={dinamicRoutes?.filter(({ redirect }) => !redirect)}
          />
          {dinamicRoutes && (
            <SiderComponent
              broken={broken}
              onBreak={setBroken}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              onShowDrawer={setVisible}
              visible={false}
              signOut={signOut}
              routes={dinamicRoutes?.filter(({ redirect }) => !redirect)}
            />
          )}
          <Layout
            style={{
              minWidth: 340,
              backgroundColor: 'var(--color-secondary)'
            }}>
            <AppHeader
              broken={broken}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              onShowDrawer={setVisible}
              visible={visible}
              userInfo={user}
            />
            {!broken && (
              <div className="ml-5 mt-3">
                {!staffDashboardCondition && <AppBreadCmb />}
                <PageHeader />
              </div>
            )}
            <Content
              className="px-0 rounded-t-xl lg:py-2 lg:px-5"
              style={{ contain: 'strict', marginTop: 0 }}>
              <Spin spinning={loading} size="large">
                <AppRoutes routes={routesResult} />
              </Spin>
            </Content>
          </Layout>
        </>
      )}
    </Layout>
  );
}

/**
 * Component definition of proptypes.
 */
DashboardView.propTypes = {
  actions: PropTypes.shape({
    setPatientIndicators: PropTypes.func.isRequired
  }).isRequired,
  authActions: PropTypes.shape({
    signOut: PropTypes.func.isRequired
  }).isRequired,
  menuActions: PropTypes.shape({
    getMainMenuByRole: PropTypes.func.isRequired
  }).isRequired,
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  loading: PropTypes.bool,
  patientActions: PropTypes.shape({
    getPatientIndicators: PropTypes.func.isRequired
  }).isRequired
};

export default DashboardView;
